import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import BrandMark from "@/components/ventrix/Logo";
import { ArrowLeft } from "lucide-react";

export function AuthShell({ title, subtitle, children, testid = "auth-shell" }) {
  return (
    <div className="min-h-screen grid place-items-center bg-[#F4FEFF] px-4 py-10" data-testid={testid}>
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-between mb-5">
          <Link to="/" className="inline-flex items-center gap-2.5" data-testid="auth-back-home">
            <BrandMark size={34} />
            <div className="leading-tight">
              <div className="font-black text-[#0E2F76]">VENTRIX</div>
              <div className="text-[10px] tracking-[0.14em] text-[#475569] font-semibold">GLOBAL FREIGHTS LTD</div>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[#475569] hover:text-[#0E2F76] font-semibold" data-testid="back-to-site"><ArrowLeft size={14}/> Back</Link>
        </div>
        <div className="vtx-card p-6 sm:p-8">
          <h1 className="text-2xl font-black text-[#0E2F76] tracking-tighter">{title}</h1>
          {subtitle && <p className="mt-1.5 text-[#475569] text-sm">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", totp_code: "" });
  const [mfaRequired, setMfa] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const res = await login(f.email, f.password, f.totp_code || undefined);
      if (res?.mfa_required) { setMfa(true); setBusy(false); return; }
      const role = res.user?.role;
      nav(role && role !== "customer" ? "/ops" : "/app", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your shipments, invoices and account.">
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Work email</span>
          <input type="email" required className="vtx-input" value={f.email} onChange={(e)=>setF({...f, email:e.target.value})} data-testid="login-email" />
        </label>
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Password</span>
          <input type="password" required className="vtx-input" value={f.password} onChange={(e)=>setF({...f, password:e.target.value})} data-testid="login-password" />
        </label>
        {mfaRequired && (
          <label className="block">
            <span className="vtx-eyebrow block mb-1.5">MFA code</span>
            <input inputMode="numeric" required className="vtx-input font-mono tracking-widest text-center" placeholder="123456" value={f.totp_code} onChange={(e)=>setF({...f, totp_code:e.target.value})} data-testid="login-totp" />
          </label>
        )}
        {err && <div className="text-sm text-[#d41f26] font-semibold" data-testid="login-error">{err}</div>}
        <button type="submit" disabled={busy} className="vtx-btn-primary w-full" data-testid="login-submit">
          {busy ? "Signing in…" : mfaRequired ? "Verify MFA & sign in" : "Sign in"}
        </button>
        <div className="flex justify-between text-xs text-[#475569] pt-2">
          <Link to="/forgot-password" className="hover:text-[#d41f26] font-semibold" data-testid="login-forgot">Forgot password?</Link>
          <Link to="/register" className="hover:text-[#d41f26] font-semibold" data-testid="login-register-link">Open an account</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", name: "", company: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register(f.email, f.password, f.name, f.company);
      nav("/app", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Open an account" subtitle="Get an account, ship in minutes.">
      <form onSubmit={submit} className="space-y-3" data-testid="register-form">
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Full name</span>
          <input required className="vtx-input" value={f.name} onChange={(e)=>setF({...f, name: e.target.value})} data-testid="reg-name" />
        </label>
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Company (optional)</span>
          <input className="vtx-input" value={f.company} onChange={(e)=>setF({...f, company: e.target.value})} data-testid="reg-company" />
        </label>
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Work email</span>
          <input type="email" required className="vtx-input" value={f.email} onChange={(e)=>setF({...f, email: e.target.value})} data-testid="reg-email" />
        </label>
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Password (min 8 chars)</span>
          <input type="password" required minLength={8} className="vtx-input" value={f.password} onChange={(e)=>setF({...f, password: e.target.value})} data-testid="reg-password" />
        </label>
        {err && <div className="text-sm text-[#d41f26] font-semibold" data-testid="reg-error">{err}</div>}
        <button type="submit" disabled={busy} className="vtx-btn-primary w-full" data-testid="reg-submit">{busy ? "Creating…" : "Create account"}</button>
        <div className="text-xs text-[#475569] pt-2">Already have an account? <Link to="/login" className="vtx-link">Sign in</Link></div>
      </form>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message || "Check your email for a reset link.");
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <AuthShell title="Reset your password" subtitle="Enter your email — we'll send a reset link.">
      <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Work email</span>
          <input type="email" required className="vtx-input" value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="forgot-email" />
        </label>
        {msg && <div className="text-sm text-[#0E2F76] font-semibold vtx-card p-3">{msg}</div>}
        <button className="vtx-btn-primary w-full" disabled={busy} data-testid="forgot-submit">{busy ? "Sending…" : "Send reset link"}</button>
        <div className="text-xs text-[#475569] pt-2"><Link to="/login" className="vtx-link">Back to sign in</Link></div>
      </form>
    </AuthShell>
  );
}
